import { Avatar, Popover } from 'antd';
import { APPOINTMENT_STATUS } from 'features/appointment/services/constants';
import userActions from 'features/user/services/actions';
import { IAptGroupByTimeAppointmentData } from 'features/user/services/types/apt';
import { customerEditorRef } from 'features/user/widgets/WaitingList/CustomerEditor';
import { CustomerInfoPopoverUI, useCustomerInfoPopover } from 'features/user/widgets/WaitingList/WalkIn/components/CustomerName';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';

type Props = {
  apt: IAptGroupByTimeAppointmentData;
  onClick?: () => void;
};

const formatClock = (value: string) => {
  const m = moment(value);
  return m.isValid() ? m.format('hh:mm A') : value;
};

const serviceLine = (apt: IAptGroupByTimeAppointmentData) => {
  const names = apt.items.map((i) => i.serviceName).filter(Boolean);
  return [...new Set(names)].join(', ');
};

const AppointmentCard = ({ apt, onClick }: Props) => {
  const timeRange = `${formatClock(apt.startTime)} - ${formatClock(apt.endTime)}`;
  const services = serviceLine(apt);
  const note = (apt as IAptGroupByTimeAppointmentData & { note?: string | null }).note;

  const staffSlots = useMemo(() => {
    const merged = new Map<string, IAptGroupByTimeAppointmentData['items'][number]>();
    apt.items.forEach((item) => {
      const prev = merged.get(item.staffId);
      if (!prev) {
        merged.set(item.staffId, { ...item });
      } else {
        merged.set(item.staffId, {
          ...prev,
          requestStaff: prev.requestStaff || item.requestStaff,
        });
      }
    });
    return Array.from(merged.values()).sort(
      (a, b) => a.indexWorking - b.indexWorking,
    );
  }, [apt.items]);

  return (
    <CardOuter type='button' onClick={onClick}>
      <CardInner>
        <TopRow>
          <CustomerBox id={apt.customerId}>
            <NameRow>
              <InfoIcon aria-hidden />
              <CustomerName title={apt.customerName}>{apt.customerName}</CustomerName>
              {apt.status === APPOINTMENT_STATUS.CONFIRM && <StatusCheck aria-hidden />}
            </NameRow>
          </CustomerBox>
          <AvatarCluster>
            {staffSlots.map((item) => (
              <AvatarWrap key={`${apt.appointmentId}-${item.staffId}`}>
                <StyledAvatar
                  src={item.staffAvatar || undefined}
                  size={40}
                  style={{
                    zoom: 1.5,
                    backgroundColor: 'rgba(245, 106, 0, 0.3)',
                    border: '1px solid rgb(245, 106, 0)',
                  }}
                >
                  {item.staffName?.[0] ?? '?'}
                </StyledAvatar>
                {item.requestStaff ? <RequestBadge>R</RequestBadge> : null}
              </AvatarWrap>
            ))}
          </AvatarCluster>
        </TopRow>

        <ServiceStrip>
          <ServiceTime>{timeRange}</ServiceTime>
          <ServiceCodes title={services}>{services}</ServiceCodes>
        </ServiceStrip>

        {note ? <NoteLine>Note: {note}</NoteLine> : null}
      </CardInner>
    </CardOuter>
  );
};

export default AppointmentCard;

type CustomerBoxProps = {
  id: string;
  children?: any;
};
const CustomerBox = ({ id, children }: CustomerBoxProps) => {
  const dispatch = useAppDispatch();
  const {
    customerInfo,
    customerDetailLoading,
    viewCustomer,
    open,
    setOpen,
    handleCheckPassBeforeView,
    dataPin,
  } = useCustomerInfoPopover(id);

  const onOpenChange = (val: boolean) => handleCheckPassBeforeView(val);
  const onEdit = () => {
    customerEditorRef.current?.openWithId(customerInfo.customerId, {
      addon: customerInfo,
      onSuccess: (data) => {
        if (data) {
          dispatch(userActions.syncCustomerInfo(data));
        }
      },
    });
    setOpen(false);
  };

  return (
    <WrapperInfoBox>
      <Popover
        placement='rightTop'
        title={null}
        content={
          <div onClick={(e) => e.stopPropagation()}>
            <CustomerInfoPopoverUI
              loading={customerDetailLoading}
              customerInfo={customerInfo}
              onOpenChange={onOpenChange}
              onEdit={onEdit}
              dataPin={dataPin}
            />
          </div>
        }
        open={open}
        trigger={'click'}
        onOpenChange={onOpenChange}
        overlayInnerStyle={{ padding: 16, borderRadius: 8, background: '#E4F4F6' }}
      >
        <NameBlock type='button' onClick={viewCustomer}>
          {children}
        </NameBlock>
      </Popover>
    </WrapperInfoBox>
  );
};
const WrapperInfoBox = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  position: relative;
  align-self: center;
  .visited {
    white-space: nowrap;
    font-weight: 700;
    font-size: 1rem;
    text-align: right;
  }
`;
const CardOuter = styled.button`
  background: #ffffff;
  border: 1px solid #86909c;
  border-radius: 4px;
  padding-bottom: 8px;
  width: 100%;

  &:active {
    opacity: 0.6;
    cursor: pointer;
  }
`;

const CardInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0 8px;
  width: 100%;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
`;

const NameBlock = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
`;

const InfoIcon = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f58220;
  position: relative;

  &::after {
    content: 'i';
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
    color: #ffffff;
  }
`;

const CustomerName = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 22px;
  color: #1d2129;
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StatusCheck = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #0da472;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 6px;
    top: 4px;
    width: 5px;
    height: 9px;
    border: solid #ffffff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const AvatarCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const AvatarWrap = styled.span`
  position: relative;
  display: inline-flex;

  & + & {
    margin-left: -10px;
  }
`;

const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
  border: 1px solid #ffffff;
`;

const RequestBadge = styled.span`
  position: absolute;
  right: 4px;
  bottom: 4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 100px;
  background: #2563eb;
  border: 1px solid #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  color: #ffffff;
  box-sizing: border-box;
`;

const ServiceStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px 8px;
  margin: 0 -8px;
  width: calc(100% + 16px);
  box-sizing: border-box;
`;

const ServiceTime = styled.span`
  flex-shrink: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 30px;
  color: #1d2129;
  text-transform: capitalize;
  white-space: nowrap;
`;

const ServiceCodes = styled.span`
  flex: 1;
  min-width: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #1d2129;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoteLine = styled.p`
  margin: 8px 0 0;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: #1d2129;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-wrap;
  text-align: left;
`;
